'use client';

import { Button, IcInfo, Typography } from '@musinsa/mds';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCuratorSnackbar } from '../../../../common/hooks/useCuratorSnackbar';
import { CampaignInfo } from './CampaignInfo';

/**
 * 콘텐츠 제작 가이드 영역 compound — Container / Title / Subsection / HashtagBox.
 *
 * - **Container**: 자식들을 묶는 wrapper. `CampaignInfo.Container` 재사용.
 * - **Title**: 제목 + 옵셔널 info ⓘ 아이콘 (클릭 시 바텀시트).
 * - **Subsection**: 라벨 + 옵셔널 안내 + 옵셔널 body (라벨/body 패턴 — 필수 해시태그, 브랜드 요청사항, 신청 번호 등).
 * - **HashtagBox**: 해시태그 회색 박스 + 복사 버튼 (Subsection 의 child 로 사용).
 *
 * 사용:
 * ```tsx
 * <ContentGuide.Container>
 *   <ContentGuide.Title onInfoClick={openSheet}>콘텐츠 제작 가이드</ContentGuide.Title>
 *   <ContentGuide.Subsection label="필수 해시태그" subtitle="#광고 해시태그는 ...">
 *     <ContentGuide.HashtagBox text={hashtagText} />
 *   </ContentGuide.Subsection>
 *   <ContentGuide.Subsection label="브랜드 요청사항" body={brandRequest} />
 *   {applicationNumber && (
 *     <ContentGuide.Subsection label="신청 번호" body={applicationNumber} />
 *   )}
 * </ContentGuide.Container>
 * ```
 */

interface TitleProps {
  children: ReactNode;
  /** 우측 info ⓘ 아이콘 노출 + 클릭 핸들러 */
  onInfoClick?: () => void;
  className?: string;
}

const Title = ({ children, onInfoClick, className }: TitleProps) => (
  <div className={twMerge('flex items-center gap-[4px]', className)}>
    <CampaignInfo.Title>{children}</CampaignInfo.Title>
    {onInfoClick && (
      <button
        type="button"
        onClick={onInfoClick}
        aria-label="콘텐츠 등록 기간 안내"
        className="inline-flex items-center"
      >
        <IcInfo width={20} height={20} />
      </button>
    )}
  </div>
);

interface SubsectionProps {
  /** 라벨 (예: "필수 해시태그", "브랜드 요청사항", "신청 번호") */
  label: string;
  /** 라벨 아래 보조 안내 텍스트 (예: "#광고 해시태그는 게시물 본문 최상단에 ...") */
  subtitle?: string;
  /** 본문 텍스트 — body 만 있으면 단순 텍스트 표시 */
  body?: string;
  /** body 대신 사용자 정의 child (예: HashtagBox) */
  children?: ReactNode;
  className?: string;
}

const Subsection = ({ label, subtitle, body, children, className }: SubsectionProps) => (
  <div className={twMerge('flex flex-col gap-[4px]', className)}>
    <Typography variant="body14pxMed" colorScheme="black">
      {label}
    </Typography>
    {subtitle && (
      <Typography variant="body13pxReg" colorScheme="gray-600">
        {subtitle}
      </Typography>
    )}
    {children}
    {body && (
      // 줄바꿈(\n) 을 그대로 표시 — 브랜드 요청사항 등 멀티라인 보존. (SID-4064)
      <Typography
        variant="body13pxReg"
        colorScheme="gray-600"
        className="block whitespace-pre-line"
      >
        {body}
      </Typography>
    )}
  </div>
);

interface HashtagBoxProps {
  /** 해시태그 원본 텍스트 (복사 대상). 박스 안에 노출. */
  text: string;
  /** 복사 버튼 라벨 (default = "복사하기") */
  copyLabel?: string;
  className?: string;
}

const HashtagBox = ({ text, copyLabel = '복사하기', className }: HashtagBoxProps) => {
  const snackbar = useCuratorSnackbar();
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      snackbar.show({ message: '해시태그가 복사되었습니다.' });
    } catch {
      snackbar.show({ message: '복사에 실패했습니다.' });
    }
  };
  return (
    <div
      className={twMerge(
        'bg-gray-100 rounded-[4px] px-[12px]! py-[10px]! flex items-center gap-[8px]',
        className,
      )}
    >
      {/* 해시태그 박스 텍스트 — Body/12_regular, gray-600. (SID-4425) */}
      <Typography variant="body12pxReg" colorScheme="gray-600" className="flex-1">
        {text}
      </Typography>
      <Typography size="13" weight="medium" asChild>
        <Button variant="tertiaryOutlinedGray" size={24} className="shrink-0" onClick={handleCopy}>
          {copyLabel}
        </Button>
      </Typography>
    </div>
  );
};

export const ContentGuide = {
  Container: CampaignInfo.Container,
  Title,
  Subsection,
  HashtagBox,
};
